import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { PageHeader, Card, Btn, Table, Badge, InfoAlert, FilterSelect, SearchInput, FilterBar } from '../components/UI';

/**
 * Access point → lodge mapping.
 *
 * A user's lodge is set from the AP their device associates with, so this
 * table is what drives lodge analytics. It is seeded from the Omada controller
 * by matching the installer's AP names; anything pinned by hand here stops
 * being auto-updated on the next sync.
 */
export default function AccessPoints() {
  const [aps, setAps]         = useState([]);
  const [lodges, setLodges]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('all');
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.getAccessPoints();
      setAps(d.access_points || []);
      setLodges(d.lodges || []);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSync() {
    setSyncing(true);
    try {
      const r = await api.syncAccessPoints();
      toast(r.message || 'Synced.', 'success');
      if (r.unmapped?.length) {
        toast(`${r.unmapped.length} AP(s) could not be matched to a lodge.`, 'warning');
      }
      load();
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setSyncing(false);
    }
  }

  async function reassign(mac, lodge_id) {
    try {
      await api.updateAccessPoint(mac, { lodge_id: lodge_id || null });
      toast('Mapping updated. It will no longer be overwritten by sync.', 'success');
      load();
    } catch (e) {
      toast(e.message, 'error');
    }
  }

  const visible = aps.filter(ap => {
    if (filter === 'unmapped' && ap.lodge_id) return false;
    if (filter === 'manual'   && ap.auto_mapped) return false;
    if (filter !== 'all' && filter !== 'unmapped' && filter !== 'manual' && ap.lodge_id !== filter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (ap.name || '').toLowerCase().includes(q) || ap.mac.toLowerCase().includes(q);
  });

  const unmappedAps = aps.filter(a => !a.lodge_id && a.type === 'ap').length;

  const columns = [
    {
      key: 'name', label: 'Device',
      render: (ap) => (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--text)' }}>
            {ap.name || <span style={{ color: 'var(--text2)', fontStyle: 'italic' }}>unnamed</span>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'monospace' }}>{ap.mac}</div>
        </div>
      ),
    },
    { key: 'type', label: 'Type', render: (ap) => <Badge color={ap.type === 'switch' ? 'purple' : 'blue'}>{ap.type}</Badge> },
    { key: 'model', label: 'Model', render: (ap) => <span style={{ color: 'var(--text2)', fontSize: 12 }}>{ap.model || '—'}</span> },
    {
      key: 'lodge', label: 'Lodge',
      render: (ap) => (
        <select
          value={ap.lodge_id || ''}
          onChange={(e) => reassign(ap.mac, e.target.value)}
          style={{
            background: 'var(--surface2)', color: 'var(--text)',
            border: '1px solid var(--border2)', borderRadius: 7,
            padding: '5px 8px', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
          }}
        >
          <option value="">— unmapped —</option>
          {lodges.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      ),
    },
    {
      key: 'auto_mapped', label: 'Source',
      render: (ap) => ap.auto_mapped
        ? <Badge color="gray">auto</Badge>
        : <Badge color="yellow">manual</Badge>,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Access Points"
        subtitle="Each AP belongs to a lodge. Users are assigned to a lodge from the AP they connect through."
        action={<Btn onClick={handleSync} loading={syncing}>Sync from controller</Btn>}
      />

      <InfoAlert type={unmappedAps ? 'warning' : 'info'}>
        {unmappedAps
          ? `${unmappedAps} access point(s) are not mapped to a lodge — users connecting through them will not be assigned one.`
          : 'Every access point is mapped. Switches may be unmapped when they feed more than one lodge.'}
        {' '}Changing a lodge here pins it, and the controller sync will leave it alone from then on.
      </InfoAlert>

      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search name or MAC…" />
        <FilterSelect
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all',      label: 'All devices' },
            { value: 'unmapped', label: 'Unmapped only' },
            { value: 'manual',   label: 'Manually pinned' },
            ...lodges.map(l => ({ value: l.id, label: l.name })),
          ]}
        />
      </FilterBar>

      <Card style={{ padding: 0, marginTop: 16 }}>
        <Table columns={columns} rows={visible} loading={loading} emptyText="No access points found." />
      </Card>
    </div>
  );
}
